// src/components/QueryGenerator.js
import React, { useState } from "react";
import "./QueryGenerator.css";
import { FaCopy, FaTimes } from "react-icons/fa";
import { useAnalysisResult } from "../../context/AnalysisResultContext";

const PRODUCT_OPTIONS = [
  { key: "kql", label: "Microsoft KQL" },
  { key: "spl", label: "Splunk SPL" },
  { key: "crowdstrike", label: "CrowdStrike" },
  { key: "google_secops", label: "Google SecOps" }
];

const PRESET_USE_CASES = [
  {
    id: "lateral-movement",
    label: "Lateral movement via remote logons",
    text:
      "Detect potential lateral movement within the network using remote logon events (RDP, SMB, WMI) and suspicious process creation across Windows endpoints."
  },
  {
    id: "suspicious-powershell",
    label: "Suspicious PowerShell activity",
    text:
      "Detect suspicious PowerShell activity related to execution of encoded commands, downloading payloads, or modifying security controls on Windows endpoints."
  },
  {
    id: "brute-force",
    label: "Brute force / credential stuffing",
    text:
      "Detect possible brute force or credential stuffing attempts based on repeated failed logons from the same IP or user within a short time window."
  },
  {
    id: "data-exfil",
    label: "Data exfiltration indicators",
    text:
      "Detect potential data exfiltration based on unusually large outbound data transfers, uploads to cloud storage or file-sharing services, and rare external destinations."
  }
];

// Extended library used by the Use case library modal
const LIBRARY_USE_CASES = [
  ...PRESET_USE_CASES,
  {
    id: "priv-esc",
    label: "Privilege escalation on endpoints",
    text:
      "Detect potential privilege escalation on Windows endpoints by monitoring token elevation, admin group membership changes, and execution of high-risk binaries from non-standard locations."
  },
  {
    id: "mfa-fatigue",
    label: "MFA fatigue attacks",
    text:
      "Detect possible MFA fatigue attacks by correlating repeated MFA prompts or push notifications for the same user account within a short time window."
  },
  {
    id: "cloud-impossible-travel",
    label: "Cloud impossible travel logins",
    text:
      "Detect impossible travel logins to cloud applications by correlating user sign-ins from distant geographic locations within an unrealistically short time window."
  },
  {
    id: "ransomware-encryption",
    label: "Ransomware-style encryption behaviour",
    text:
      "Detect possible ransomware behaviour by monitoring high-volume file modifications, creation of ransom note filenames, and disabling of shadow copies or backup services."
  },
  {
    id: "command-and-control",
    label: "Command and control beacons",
    text:
      "Detect potential command-and-control beaconing by looking for periodic, low-volume outbound connections to rare domains or IP addresses over uncommon ports."
  },
  {
    id: "suspicious-service-creation",
    label: "Suspicious service creation",
    text:
      "Detect suspicious Windows service creation events where the service binary path points to temp folders, user profiles, or unusual executables."
  }
];

const DEFAULT_PRODUCTS = ["kql", "spl"];

const QueryGenerator = () => {
  const {
    queryGenInput,
    setQueryGenInput,
    queryGenResult: result,
    setQueryGenResult: setResult,
    clearQueryGenResult,
  } = useAnalysisResult();

  // Derive controlled inputs from the persisted state (so they survive nav)
  const useCase = queryGenInput?.useCase ?? "";
  const selectedProducts = queryGenInput?.platforms?.length ? queryGenInput.platforms : DEFAULT_PRODUCTS;
  const setUseCase = (v) =>
    setQueryGenInput((prev) => ({ ...(prev || {}), useCase: typeof v === "function" ? v(prev?.useCase ?? "") : v, platforms: prev?.platforms ?? DEFAULT_PRODUCTS }));
  const setSelectedProducts = (v) =>
    setQueryGenInput((prev) => ({ ...(prev || {}), useCase: prev?.useCase ?? "", platforms: typeof v === "function" ? v(prev?.platforms ?? DEFAULT_PRODUCTS) : v }));

  const [activeTab, setActiveTab] = useState("kql");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [copiedForKey, setCopiedForKey] = useState(null);

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");

  const hasResults = !!result && Object.keys(result).length > 0;

  const handlePresetClick = (preset) => {
    setUseCase(preset.text);
    setError("");
    setInfoMessage("");
  };

  const toggleProduct = (key) => {
    setSelectedProducts((prev) => {
      if (prev.includes(key)) {
        const updated = prev.filter((p) => p !== key);
        if (updated.length && !updated.includes(activeTab)) {
          setActiveTab(updated[0]);
        }
        return updated;
      }
      const updated = [...prev, key];
      if (!activeTab) setActiveTab(key);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setResult(null);
    setCopiedForKey(null);

    const trimmed = useCase.trim();
    if (!trimmed) {
      setError("Describe what you want to detect.");
      return;
    }
    if (!selectedProducts.length) {
      setError("Select at least one platform.");
      return;
    }

    setLoading(true);

    try {
      const token = sessionStorage.getItem("auth_token");

      const res = await fetch("/api/query-generator/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          useCase: trimmed,
          products: selectedProducts
        })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        const message =
          data?.error || `Query generation failed with status ${res.status}.`;
        setError(message);
        return;
      }

      const payload = data.data || {};
      setResult(payload);

      const keys = Object.keys(payload);
      if (keys.length) {
        setActiveTab(keys[0]);
      }

      setInfoMessage("Queries generated. Review and tune before use.");
    } catch (err) {
      console.error("Query generator frontend error:", err);
      setError("Unexpected error while generating queries. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyQuery = (key) => {
    if (!result || !result[key] || !result[key].query) return;
    const text = result[key].query;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedForKey(key);
        setInfoMessage("");
        setTimeout(() => setCopiedForKey(null), 2500);
      })
      .catch(() => {
        setError("Unable to copy to clipboard. Please copy manually.");
      });
  };

  const handleLibrarySelect = (uc) => {
    setUseCase(uc.text);
    setLibraryOpen(false);
    setLibrarySearch("");
    setError("");
    setInfoMessage("");
  };

  const filteredLibrary = LIBRARY_USE_CASES.filter((uc) => {
    const q = librarySearch.toLowerCase();
    if (!q) return true;
    return (
      uc.label.toLowerCase().includes(q) || uc.text.toLowerCase().includes(q)
    );
  });

  const renderResultCard = (key) => {
    if (!result || !result[key]) return null;
    const { query, description, notes } = result[key];

    const label =
      PRODUCT_OPTIONS.find((p) => p.key === key)?.label || key.toUpperCase();

    return (
      <div className="pmq-result-card">
        <div className="pmq-result-header">
          <div>
            <h3 className="pmq-result-title">{label}</h3>
            <p className="pmq-result-subtitle">
              Generated detection query for the selected scenario.
            </p>
          </div>
        </div>

        {description && (
          <div className="pmq-section">
            <h4 className="pmq-section-title">Detection logic</h4>
            <p className="pmq-section-body">{description}</p>
          </div>
        )}

        {query && (
          <div className="pmq-section">
            <div className="pmq-query-header">
              <h4 className="pmq-section-title">Query</h4>
              <button
                type="button"
                className="pmq-copy-icon-btn"
                onClick={() => handleCopyQuery(key)}
              > 
                {copiedForKey === key ? "Copied" : "Copy"}
                <span>
                    &nbsp;<FaCopy />
                </span>
                
              </button>
            </div>
            <pre className="pmq-query-block">
              <code>{query}</code>
            </pre>
          </div>
        )}

        {notes && (
          <div className="pmq-section">
            <h4 className="pmq-section-title">Tuning notes</h4>
            <p className="pmq-section-body pmq-section-body-muted">{notes}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pmq-root">
      <div className="pmq-shell">
        <header className="pmq-header">
          <div>
            <h2 className="pmq-title">Threat Hunt Query Generator</h2>
            <p className="pmq-subtitle">
              Tell the copilot what you want to detect and generate ready-to-tune
              queries for your SIEM.
            </p>
          </div>
        </header>

        <div className="pmq-main-card">
          {/* Input box */}
          <form onSubmit={handleSubmit} className="pmq-input-form">
            <div className="pmq-input-wrapper">
              <textarea
                className="pmq-input-box"
                rows={3}
                placeholder="Message the query copilot: e.g. 'Provide a KQL query for checking lateral movement of a threat actor within Defender.'"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
              />
            </div>

            {/* Presets + Library */}
            <div className="pmq-presets-actions">
              <div className="pmq-presets-row">
                {PRESET_USE_CASES.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="pmq-preset-chip"
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="pmq-library-btn"
                onClick={() => setLibraryOpen(true)}
              >
                Use case library
              </button>
            </div>

            {/* Platforms */}
            <div className="pmq-platform-row">
              <span className="pmq-platform-label">Target platforms</span>
              <div className="pmq-chip-group">
                {PRODUCT_OPTIONS.map(({ key, label }) => {
                  const active = selectedProducts.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={
                        "pmq-chip" + (active ? " pmq-chip-active" : "")
                      }
                      onClick={() => toggleProduct(key)}
                    >
                      <span className="pmq-chip-label">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Generate – centered */}
<div className="pmq-generate-row">
  <button
    type="submit"
    className="pmq-neon-btn"
    disabled={loading}
  >
    {loading ? "Generating..." : "Generate"}
  </button>
</div>


            {error && <div className="pmq-alert pmq-alert-error">{error}</div>}

            {infoMessage && !error && (
              <div className="pmq-alert pmq-alert-info">{infoMessage}</div>
            )}
          </form>

          {/* Results only after generation */}
          {hasResults && (
            <section className="pmq-results-shell">
              <div className="pmq-results-header pmq-results-header-row">
                <div>
                  <h3 className="pmq-results-title">Generated queries</h3>
                  <p className="pmq-results-subtitle">
                    Validate and adapt these templates to your environment before
                    deploying to production.
                  </p>
                </div>
                <button
                  type="button"
                  className="pmq-clear-btn"
                  onClick={clearQueryGenResult}
                  title="Clear results"
                  aria-label="Clear results"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="pmq-tabs">
                {Object.keys(result).map((key) => {
                  const label =
                    PRODUCT_OPTIONS.find((p) => p.key === key)?.label ||
                    key.toUpperCase();
                  const isActive = key === activeTab;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={
                        "pmq-tab" + (isActive ? " pmq-tab-active" : "")
                      }
                      onClick={() => setActiveTab(key)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              <div className="pmq-tab-panel">{renderResultCard(activeTab)}</div>
            </section>
          )}
        </div>

        {/* Use case library modal */}
        {libraryOpen && (
          <div className="pmq-library-backdrop">
            <div className="pmq-library-card">
              <div className="pmq-library-header">
                <h3>Use case library</h3>
                <button
                  type="button"
                  className="pmq-library-close"
                  onClick={() => setLibraryOpen(false)}
                >
                  ✕
                </button>
              </div>

              <input
                className="pmq-library-search"
                placeholder="Search use cases (e.g. ransomware, MFA, brute force)"
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
              />

              <div className="pmq-library-list">
                {filteredLibrary.map((uc) => (
                  <button
                    key={uc.id}
                    type="button"
                    className="pmq-library-item"
                    onClick={() => handleLibrarySelect(uc)}
                  >
                    <div className="pmq-library-item-title">{uc.label}</div>
                    <div className="pmq-library-item-text">{uc.text}</div>
                  </button>
                ))}

                {!filteredLibrary.length && (
                  <div className="pmq-library-empty">
                    No use cases match that search.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryGenerator;
